import React, { useState, useRef, useEffect } from 'react';
import { FaPaperPlane } from 'react-icons/fa';

/**
 * ChatInput - Text input component for sending messages
 *
 * Features:
 * - Multi-line input (grows up to 3 lines)
 * - Enter to send, Shift+Enter for newline
 * - Character limit (500 chars)
 * - Disabled state during loading
 */
const ChatInput = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  const MAX_CHARS = 500;

  /**
   * Handle sending message
   */
  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend(message);
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  /**
   * Handle key press (Enter to send)
   */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Auto-resize textarea as user types
   */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 80); // Max 3 lines (~80px)
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  const charsRemaining = MAX_CHARS - message.length;
  const isOverLimit = charsRemaining < 0;

  return (
    <div
      style={{
        borderTop: '1px solid #e0e0e0',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '0 0 12px 12px'
      }}
    >
      {/* Character counter (only show when getting close to limit) */}
      {charsRemaining < 100 && (
        <div
          style={{
            fontSize: '11px',
            color: isOverLimit ? '#dc3545' : '#999',
            marginBottom: '8px',
            textAlign: 'right'
          }}
        >
          {charsRemaining} characters remaining
        </div>
      )}

      {/* Input area */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me about properties, inspections, work orders..."
          disabled={disabled}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid #ddd',
            borderRadius: '20px',
            fontSize: '14px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            minHeight: '40px',
            maxHeight: '80px',
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            color: disabled ? '#999' : '#333',
            transition: 'border-color 0.2s',
            overflow: 'hidden'
          }}
          onFocus={(e) => {
            if (!disabled) {
              e.target.style.borderColor = '#0056b3';
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#ddd';
          }}
        />

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={disabled || !message.trim() || isOverLimit}
          style={{
            backgroundColor: disabled || !message.trim() || isOverLimit ? '#ccc' : '#0056b3',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || !message.trim() || isOverLimit ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            if (!disabled && message.trim() && !isOverLimit) {
              e.currentTarget.style.backgroundColor = '#004494';
              e.currentTarget.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && message.trim() && !isOverLimit) {
              e.currentTarget.style.backgroundColor = '#0056b3';
              e.currentTarget.style.transform = 'scale(1)';
            }
          }}
          title={isOverLimit ? 'Message too long' : 'Send message (Enter)'}
        >
          <FaPaperPlane size={16} />
        </button>
      </div>

      {/* Helper text */}
      <div
        style={{
          fontSize: '11px',
          color: '#999',
          marginTop: '8px',
          textAlign: 'center'
        }}
      >
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default ChatInput;
